// Portal Utilities - Shared helpers for Staff Portal components
import axios from "axios";

// API base URL from environment
export const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Wave 2 · Interaction 3 — canonical axios request interceptor.
// Reads the token from localStorage at REQUEST time (not at effect mount),
// which eliminates the auth-hydration race where a component's initial
// `getAuthHeaders()` snapshot fires before the login redirect commits.
// Idempotent: only installs once.
if (typeof window !== "undefined" && !window.__portalAxiosAuthInstalled) {
  axios.interceptors.request.use((config) => {
    try {
      const t = localStorage.getItem("token");
      if (t) {
        config.headers = config.headers || {};
        // Only overwrite if the caller did not already set a valid one.
        const existing = config.headers.Authorization || config.headers.authorization;
        if (!existing || String(existing).endsWith("null") || String(existing).endsWith("undefined")) {
          config.headers.Authorization = `Bearer ${t}`;
        }
      }
    } catch (_) { /* localStorage unavailable */ }
    return config;
  });
  window.__portalAxiosAuthInstalled = true;
}

// Get auth headers for API calls
export const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    headers: { Authorization: `Bearer ${token}` }
  };
};

// Check if user is admin (owner, partner, or receptionist for certain permissions)
export const isAdmin = (role) => {
  return ["receptionist", "owner", "partner"].includes(role);
};

// Check if user is owner or partner
export const isOwnerOrPartner = (role) => {
  return role === "owner" || role === "partner";
};

// Format date for display
export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Format time for display
export const formatTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

// Check if a date is "today" (using effective today based on business hours)
export const isToday = (dateStr) => {
  return dateStr === getEffectiveTodayStr();
};

// Staff color configuration
export const STAFF_COLORS = {
  "Tina": { color: "#3B82F6", bg: "bg-blue-500", bgLight: "bg-blue-100", border: "border-blue-500", text: "text-blue-700" },
  "Kim": { color: "#10B981", bg: "bg-emerald-500", bgLight: "bg-emerald-100", border: "border-emerald-500", text: "text-emerald-700" },
  "Kathy": { color: "#8B5CF6", bg: "bg-violet-500", bgLight: "bg-violet-100", border: "border-violet-500", text: "text-violet-700" },
  "Sandy": { color: "#F59E0B", bg: "bg-amber-500", bgLight: "bg-amber-100", border: "border-amber-500", text: "text-amber-700" }
};

// Business hours configuration
export const BUSINESS_HOURS = {
  sunday: { open: "11:00", close: "17:30" },
  monday: { open: "09:30", close: "19:30" },
  tuesday: { open: "09:30", close: "19:30" },
  wednesday: { open: "09:30", close: "19:30" },
  thursday: { open: "09:30", close: "19:30" },
  friday: { open: "09:30", close: "19:30" },
  saturday: { open: "09:30", close: "19:00" }
};

// Weekday names for lookup (JS getDay(): 0=Sunday, 1=Monday, etc.)
const WEEKDAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
export { WEEKDAY_NAMES };

// Check if a time is within business hours for a given date
export const isWithinBusinessHours = (dateStr, startTime, endTime) => {
  if (!dateStr || !startTime) return { valid: true };
  
  // Get day of week from the date
  const date = new Date(dateStr + "T12:00:00");
  const weekdayIndex = date.getDay();
  const weekdayName = WEEKDAY_NAMES[weekdayIndex];
  const hours = BUSINESS_HOURS[weekdayName];
  
  if (!hours) return { valid: true };
  
  // Convert times to minutes
  const timeToMinutes = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  
  const openMinutes = timeToMinutes(hours.open);
  const closeMinutes = timeToMinutes(hours.close);
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = endTime ? timeToMinutes(endTime) : startMinutes + 60;
  
  // Check if start time is before opening
  if (startMinutes < openMinutes) {
    return { 
      valid: false, 
      error: `Start time is before opening (${formatMilitaryTo12Hour(hours.open)})`,
      openTime: hours.open,
      closeTime: hours.close
    };
  }
  
  // Check if start time is after closing
  if (startMinutes >= closeMinutes) {
    return { 
      valid: false, 
      error: `Start time is after closing (${formatMilitaryTo12Hour(hours.close)})`,
      openTime: hours.open,
      closeTime: hours.close
    };
  }
  
  // Check if end time extends past closing
  if (endMinutes > closeMinutes) {
    return { 
      valid: false, 
      error: `Appointment extends past closing time (${formatMilitaryTo12Hour(hours.close)})`,
      openTime: hours.open,
      closeTime: hours.close
    };
  }
  
  return { valid: true, openTime: hours.open, closeTime: hours.close };
};

// Get the "effective today" for calendar purposes
// Today persists until 1 hour after business closes for that day
// All times in Central Time (America/Chicago)
export const getEffectiveToday = () => {
  // Get current time in Central Time
  const now = new Date();
  const centralTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
  
  const currentHour = centralTime.getHours();
  const currentMinute = centralTime.getMinutes();
  const currentTotalMinutes = currentHour * 60 + currentMinute;
  
  const weekdayIndex = centralTime.getDay(); // 0=Sunday, 1=Monday, etc.
  const weekdayName = WEEKDAY_NAMES[weekdayIndex];
  const todayHours = BUSINESS_HOURS[weekdayName];
  
  // Parse closing time and add 1 hour buffer
  const [closeHour, closeMinute] = todayHours.close.split(":").map(Number);
  const closeWithBufferMinutes = (closeHour * 60 + closeMinute) + 60; // Add 1 hour buffer
  
  // If current time is past closing + 1 hour, "effective today" is tomorrow
  if (currentTotalMinutes >= closeWithBufferMinutes) {
    const tomorrow = new Date(centralTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }
  
  // Otherwise, return today (at midnight for clean date comparison)
  const today = new Date(centralTime);
  today.setHours(0, 0, 0, 0);
  return today;
};

// Salon-timezone-safe YYYY-MM-DD string from a Date that already holds the
// salon (Central) calendar fields. CRITICAL: never use `.toISOString()` for
// calendar dates — that converts to UTC and rolls the date back/forward for
// non-UTC browsers (e.g. a receptionist in the Philippines would see
// "yesterday"). We read the LOCAL Y/M/D fields, which getEffectiveToday()
// has already pinned to America/Chicago.
export const toDateStr = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Salon "today" as a YYYY-MM-DD string, pinned to America/Chicago regardless
// of the viewer's browser timezone. Use this for all calendar default dates,
// "today" comparisons, and backend date-range queries.
export const getEffectiveTodayStr = () => toDateStr(getEffectiveToday());

// Convert 24-hour time to 12-hour format
export const to12Hour = (hour24) => {
  const hour = hour24 % 12 || 12;
  const period = hour24 < 12 ? 'AM' : 'PM';
  return `${hour}:00 ${period}`;
};

// Convert 24-hour time with minutes to 12-hour format
export const to12HourWithMinutes = (hour24, minutes) => {
  const hour = hour24 % 12 || 12;
  const period = hour24 < 12 ? 'AM' : 'PM';
  return `${hour}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Convert a time string like "14:30" or "09:15" to "2:30 PM" or "9:15 AM"
export const formatTimeString = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const hour = hours % 12 || 12;
  const period = hours < 12 ? 'AM' : 'PM';
  return `${hour}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Convert 12-hour format back to 24-hour for calculations
export const from12Hour = (time12) => {
  const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return { hour: 0, minute: 0 };
  let hour = parseInt(match[1]);
  const minute = parseInt(match[2]);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
};

// Generate 30-minute slots based on business hours (12-hour format)
export const generateHourSlots = (openTime, closeTime) => {
  const slots = [];
  const [openHour, openMin = 0] = openTime.split(":").map(Number);
  const [closeHour, closeMin = 0] = closeTime.split(":").map(Number);
  
  // Start from opening time, round to nearest 30-min slot
  let currentHour = openHour;
  let currentMin = openMin < 30 ? 0 : 30;
  
  // Convert close time to total minutes for comparison
  const closeMinutes = closeHour * 60 + closeMin;
  
  while (true) {
    const currentMinutes = currentHour * 60 + currentMin;
    if (currentMinutes > closeMinutes) break;
    
    slots.push(to12HourWithMinutes(currentHour, currentMin));
    
    // Increment by 30 minutes
    currentMin += 30;
    if (currentMin >= 60) {
      currentMin = 0;
      currentHour++;
    }
  }
  
  return slots;
};

// Check if an event overlaps with a specific 30-minute slot
export const getEventsForHour = (events, hourSlot) => {
  const { hour: slotHour, minute: slotMin } = from12Hour(hourSlot);
  const slotStartMinutes = slotHour * 60 + slotMin;
  const slotEndMinutes = slotStartMinutes + 30;
  
  return (events || []).filter(event => {
    const [startH, startM] = event.start_time.split(":").map(Number);
    const [endH, endM] = event.end_time.split(":").map(Number);
    const eventStartMinutes = startH * 60 + startM;
    const eventEndMinutes = endH * 60 + endM;
    
    // Event overlaps with slot if it starts before slot ends AND ends after slot starts
    return eventStartMinutes < slotEndMinutes && eventEndMinutes > slotStartMinutes;
  });
};

// Convert military time string to 12-hour format
export const formatMilitaryTo12Hour = (timeStr) => {
  if (!timeStr) return timeStr;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Status badge colors
export const getStatusBadgeClass = (status) => {
  const colors = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    denied: "bg-red-100 text-red-800",
    scheduled: "bg-blue-100 text-blue-800",
    checked_in: "bg-green-100 text-green-800",
    completed: "bg-gray-100 text-gray-800",
    cancelled: "bg-red-100 text-red-800"
  };
  return colors[status] || colors.pending;
};
